import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { PAYMENT_TERMS, termsToNetDays } from '@accounting/shared';
import { useAccounts } from '../../hooks/useAccounts';
import { useContacts } from '../../hooks/useContacts';
import { AccountPicker } from '../accounts/AccountPicker';
import { ContactPicker } from '../contacts/ContactPicker';
import { formatCurrency, todayIso } from '../../lib/formatters';

export interface InvoiceLineDraft {
  accountId: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

export interface InvoiceFormValues {
  customerId: string;
  invoiceDate: string;
  dueDate: string;
  terms: string;
  memo: string;
  lines: InvoiceLineDraft[];
}

export interface InvoiceFormSubmit {
  /** Omitted when no customer is selected (cash sale). */
  customerId?: string;
  invoiceDate: string;
  dueDate: string;
  terms?: string;
  memo?: string;
  lines: { accountId: string; description?: string; quantity: string; unitPrice: string }[];
}

interface Props {
  companyId: string;
  initial?: Partial<InvoiceFormValues>;
  submitLabel: string;
  submitting: boolean;
  errorMessage?: string;
  cancelTo: string;
  onSubmit: (input: InvoiceFormSubmit) => void;
  /** When provided, render a "Save as draft" button that saves without issuing. */
  onSaveDraft?: (input: InvoiceFormSubmit) => void;
  /**
   * When provided, render a "Save & New" button. The handler receives a `reset`
   * callback to clear the form for the next invoice — call it from the mutation's
   * onSuccess so the form only resets after a successful save.
   */
  onSubmitAndNew?: (input: InvoiceFormSubmit, reset: () => void) => void;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const emptyLine = (): InvoiceLineDraft => ({ accountId: '', description: '', quantity: '1', unitPrice: '' });

export function InvoiceForm({ companyId, initial, submitLabel, submitting, errorMessage, cancelTo, onSubmit, onSaveDraft, onSubmitAndNew }: Props) {
  const isEdit = !!initial;
  const { data: accounts = [] } = useAccounts(companyId);
  const { data: customers = [] } = useContacts(companyId, { contactType: 'customer', includeInactive: true });

  const initialTerms = initial?.terms ?? 'Net 30';
  const [customerId, setCustomerId] = useState(initial?.customerId ?? '');
  const [invoiceDate, setInvoiceDate] = useState(initial?.invoiceDate ?? todayIso());
  const [terms, setTerms] = useState(initialTerms);
  const [dueDate, setDueDate] = useState(
    initial?.dueDate ?? addDays(initial?.invoiceDate ?? todayIso(), termsToNetDays(initialTerms)),
  );
  // On a new invoice the terms come from the customer and the due date is derived;
  // once the user edits either, we stop auto-syncing it.
  const [termsTouched, setTermsTouched] = useState(isEdit);
  const [dueDateTouched, setDueDateTouched] = useState(isEdit);
  const [memo, setMemo] = useState(initial?.memo ?? '');
  const [lines, setLines] = useState<InvoiceLineDraft[]>(
    initial?.lines && initial.lines.length > 0 ? initial.lines : [emptyLine()],
  );

  // New invoice: adopt the selected customer's default terms (until the user overrides).
  useEffect(() => {
    if (isEdit || termsTouched || !customerId) return;
    const customer = customers.find((c) => c.id === customerId);
    if (customer?.paymentTerms) setTerms(customer.paymentTerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, customers]);

  // Keep the due date aligned with terms + invoice date until the user sets it manually.
  useEffect(() => {
    if (dueDateTouched) return;
    setDueDate(addDays(invoiceDate, termsToNetDays(terms)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms, invoiceDate]);

  const presetTerms = new Set<string>(PAYMENT_TERMS);

  // Invoices credit income — only revenue / other income accounts are selectable.
  // Also keep any account already on a line (e.g. a non-revenue account picked by
  // mistake before this rule existed), so it renders and can be corrected.
  const incomeAccounts = accounts.filter(
    (a) => a.isActive && a.allowDirectPosting && (a.accountType === 'revenue' || a.accountType === 'other_income'),
  );
  const usedIds = new Set(lines.map((l) => l.accountId).filter(Boolean));
  const isLineSelectable = (a: typeof accounts[number]) =>
    (a.isActive && a.allowDirectPosting && (a.accountType === 'revenue' || a.accountType === 'other_income')) ||
    usedIds.has(a.id);

  const lineAmount = (l: InvoiceLineDraft) => (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0);
  const total = lines.reduce((s, l) => s + lineAmount(l), 0);

  const setLine = (i: number, patch: Partial<InvoiceLineDraft>) =>
    setLines((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (i: number) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  // A customer is optional — an invoice without one is a cash sale.
  const valid = total > 0 && lines.some((l) => l.accountId && lineAmount(l) > 0);

  const buildInput = (): InvoiceFormSubmit => ({
    customerId: customerId || undefined,
    invoiceDate,
    dueDate,
    terms: terms.trim() || undefined,
    memo: memo.trim() || undefined,
    lines: lines
      .filter((l) => l.accountId && lineAmount(l) > 0)
      .map((l) => ({
        accountId: l.accountId,
        description: l.description.trim() || undefined,
        quantity: String(parseFloat(l.quantity) || 0),
        unitPrice: String(parseFloat(l.unitPrice) || 0),
      })),
  });

  const submit = () => onSubmit(buildInput());

  // "Save & New": clear the form for the next invoice (called after a successful save).
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);
  const reset = () => {
    setCustomerId('');
    setLines([emptyLine()]);
    setMemo('');
    setTerms('Net 30');
    setTermsTouched(false);
    setDueDateTouched(false);
    setFlash('Invoice saved — ready for the next one.');
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 4000);
    // The invoice date is kept so a batch of same-day invoices is quick to enter.
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 16, padding: 16, maxWidth: 760 }}>
        <div className="form-grid">
          <label htmlFor="cust">Customer</label>
          <ContactPicker
            companyId={companyId}
            value={customerId}
            onChange={setCustomerId}
            contactType="customer"
            defaultNewType="customer"
            placeholder="— no customer —"
            id="cust"
          />

          <label htmlFor="idate">Invoice date</label>
          <input
            id="idate"
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            style={{ maxWidth: 200 }}
          />

          <label htmlFor="terms">Terms</label>
          <select
            id="terms"
            value={terms}
            onChange={(e) => {
              setTermsTouched(true);
              setTerms(e.target.value);
            }}
            style={{ maxWidth: 280 }}
          >
            {terms === '' && <option value="">— none —</option>}
            {terms !== '' && !presetTerms.has(terms) && <option value={terms}>{terms}</option>}
            {PAYMENT_TERMS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <label htmlFor="ddate">Due date</label>
          <input
            id="ddate"
            type="date"
            value={dueDate}
            onChange={(e) => {
              setDueDateTouched(true);
              setDueDate(e.target.value);
            }}
            style={{ maxWidth: 200 }}
          />

          <label htmlFor="memo">Memo</label>
          <input id="memo" value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ fontSize: 16, marginTop: 0 }}>Line items</h2>
        {incomeAccounts.length === 0 && (
          <div className="field-error" style={{ marginBottom: 12 }}>
            No revenue accounts found. Add a revenue account on the Chart of Accounts first.
          </div>
        )}
        <table className="table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th>Revenue account</th>
              <th>Description</th>
              <th style={{ textAlign: 'right', width: 80 }}>Qty</th>
              <th style={{ textAlign: 'right', width: 120 }}>Rate</th>
              <th style={{ textAlign: 'right', width: 130 }}>Amount</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <AccountPicker
                    accounts={accounts}
                    value={l.accountId}
                    onChange={(id) => setLine(i, { accountId: id })}
                    predicate={isLineSelectable}
                  />
                </td>
                <td>
                  <input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} style={{ width: '100%' }} />
                </td>
                <td>
                  <input inputMode="decimal" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} style={{ width: '100%', textAlign: 'right' }} />
                </td>
                <td>
                  <input
                    inputMode="decimal"
                    value={l.unitPrice}
                    onChange={(e) => setLine(i, { unitPrice: e.target.value })}
                    style={{ width: '100%', textAlign: 'right' }}
                    placeholder="0.00"
                  />
                </td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(lineAmount(l).toFixed(2))}</td>
                <td>
                  <button className="btn-danger" onClick={() => removeLine(i)} disabled={lines.length <= 1}>
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4}>Total</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(total.toFixed(2))}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        <button className="btn-secondary" onClick={addLine}>
          + Add line
        </button>

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}
        {flash && <div className="tag ok" style={{ marginTop: 12, display: 'inline-block' }}>{flash}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button className="btn-primary" onClick={submit} disabled={!valid || submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </button>
          {onSubmitAndNew && (
            <button
              className="btn-secondary"
              onClick={() => onSubmitAndNew(buildInput(), reset)}
              disabled={!valid || submitting}
              title="Issue this invoice, then start another"
            >
              Save &amp; New
            </button>
          )}
          {onSaveDraft && (
            <button
              className="btn-secondary"
              onClick={() => onSaveDraft(buildInput())}
              disabled={!valid || submitting}
              title="Save without issuing — no journal entry is posted until you issue it"
            >
              Save as draft
            </button>
          )}
          <Link to={cancelTo} className="btn-secondary" style={{ textDecoration: 'none' }}>
            Cancel
          </Link>
        </div>
      </div>
    </>
  );
}
